import { FC } from 'react';

import { RichText } from '@sitecore-jss/sitecore-jss-react';
import { UseFormRegister, FieldErrors, Controller, Control } from 'react-hook-form';

import { useFormatter } from '@i18n';
import { wrap } from '@sitecore/common';
import type { DeceasedFormRendering } from '@sitecore/types/DeceasedForm';
import { InputText, InputNumber, Heading, Text, Grid, InputDatepicker } from '@sparky';

import type { FormValues } from '../types';

type Props = {
  register: UseFormRegister<FormValues>;
  control: Control<FormValues, string>;
  fields: DeceasedFormRendering['fields']['deceasedFormFields'];
  errors: FieldErrors<FormValues>;
};

export const DeceasedInputs: FC<Props> = ({ register, fields, control, errors }) => {
  const {
    title,
    description,
    customerIdFormField,
    deceasedDateFormField,
    endContractDateFormField,
    footerDescription,
    houseNumberFormField,
    houseNumberSuffixFormField,
    postalCodeFormField,
  } = fields;
  const { date } = useFormatter();

  return (
    <>
      <Heading as="h3" size="S">
        {wrap(title)}
      </Heading>
      <Text size={{ initial: 'BodyM', md: 'BodyL' }}>{wrap(description, <RichText html={description.value} />)}</Text>
      <Grid columns={{ initial: '1', lg: '2' }} gap="6">
        <InputText
          label={postalCodeFormField?.value?.label}
          error={errors?.deceasedPostalCode?.message}
          hint={postalCodeFormField?.value?.hint}
          placeholder={postalCodeFormField?.value?.placeholder}
          {...register('deceasedPostalCode')}
        />
        <InputText
          label={houseNumberFormField?.value?.label}
          error={errors?.deceasedHouseNumber?.message}
          hint={houseNumberFormField?.value?.hint}
          placeholder={houseNumberFormField?.value?.placeholder}
          {...register('deceasedHouseNumber')}
        />
        <InputText
          label={houseNumberSuffixFormField?.value?.label}
          error={errors?.deceasedHouseNumberSuffix?.message}
          hint={houseNumberSuffixFormField?.value?.hint}
          placeholder={houseNumberSuffixFormField?.value?.placeholder}
          {...register('deceasedHouseNumberSuffix')}
          isOptional
        />
        <InputNumber
          label={customerIdFormField.value.label}
          error={errors?.customerId?.message}
          {...register('customerId')}
          formatOptions={{ useGrouping: false }}
        />
        <Controller
          control={control}
          name="deceasedDate"
          render={({ field: { onChange, value, name } }) => (
            <InputDatepicker
              name={name}
              isReadOnly
              label={deceasedDateFormField.value.label}
              value={value}
              onDaySelect={d => onChange(d && date.short(d as Date))}
              fromYear={2000}
              toDate={new Date()}
              error={errors.deceasedDate?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="endContractDate"
          render={({ field: { onChange, value, name } }) => (
            <InputDatepicker
              name={name}
              isReadOnly
              label={endContractDateFormField.value.label}
              value={value}
              onDaySelect={d => onChange(d && date.short(d as Date))}
              fromYear={2000}
              error={errors.endContractDate?.message}
            />
          )}
        />
      </Grid>
      <Text size={{ initial: 'BodyM', md: 'BodyL' }}>
        {wrap(footerDescription, <RichText html={footerDescription.value} />)}
      </Text>
    </>
  );
};
